import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import moment, { Moment } from "moment";
import { Link } from "react-router-dom";
import { Form } from "react-bootstrap";
import _ from "lodash";

import pcsIcon from "assets/svg/pcs.svg";
import TimeRangeDropdown, {
    TimeRangeSelection,
} from "components/common/TimeRangeDropdown";
import {
    CHART_CUSTOM_RANGE_OPTION,
    LastSixMonthsTimeSelection,
} from "constant";
import { convertSecondsToPrometheusTime, isHttpSuccess } from "utils/functions";
import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import {
    getGatewayAlertNotification,
    updateAlertNotification,
} from "service/gatewayService";
import { walletModalTrigger } from "hooks/wallet";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { getCalendarFormat } from "utils/datetime";

const StyledContainer = styled.div`
    display: flex;
    padding: 17px 30px;
    align-items: center;
    gap: 17px;

    > div {
        flex: 1;
    }

    .gateway-name {
        gap: 10px;
        display: flex;
        align-items: center;

        .icon {
            width: 36px;
            height: 36px;
            border-radius: 50%;
        }
    }

    .label {
        color: #828fab;
        font-size: 14px;
        display: flex;
        align-items: center;
        gap: 5px;
    }

    a,
    .sensor-value {
        color: #00cbaf;
    }

    .action {
        display: flex;
        justify-content: space-between;
        align-items: center;

        > div {
        }

        .custom-switch {
            padding-left: 2.25rem; // Rollback to default padding of bootstrap
        }

        .label > span:first-child {
            position: relative;
            top: 2px;
        }
    }
`;

const DefaultTimeRange = LastSixMonthsTimeSelection[6].time;
const timeRanges = LastSixMonthsTimeSelection.reduce<Record<string, Moment[]>>(
    (acc, timeSelection) => {
        const { time, timeGapToNow } = timeSelection;
        acc[`${time}`] = [moment().subtract(timeGapToNow, "seconds"), moment()];
        return acc;
    },
    {}
);

const PSCGatewayDetail = ({
    lastActive,
    gateway,
    deviceData = [],
    onChange,
    onLoading,
}: any) => {
    const device = _.get(gateway, "info.devices[0]", {});
    const deviceSaid = "0";
    const [disabledAlert, setDisabledAlert] = useState(true);
    const [alertObject, setAlertObject] = useState<any>({});
    const [isAlert, setIsAlert] = useState(false);
    const [timeRangeSelected, setTimeRangeSelected] =
        useState<TimeRangeSelection>({
            range: DefaultTimeRange,
            startDate: timeRanges[DefaultTimeRange][0],
            endDate: timeRanges[DefaultTimeRange][1],
        });
    const sensorData = {
        value: _.get(deviceData, "value[0]", "-"),
        min: _.get(deviceData, "min[0]", "-"),
        max: _.get(deviceData, "max[0]", "-"),
    };

    useEffect(() => {
        getGatewayAlertNotification(gateway.gateway_id).then(
            ({ data, status }) => {
                if (isHttpSuccess(status)) {
                    const alertObject = data.find((item: any) => {
                        return (
                            item.device_id === device.UID &&
                            item.said === Number(deviceSaid)
                        );
                    });
                    setDisabledAlert(!alertObject);
                    setAlertObject(alertObject);
                    setIsAlert(alertObject && alertObject.enabled === true);
                }
            }
        );
    }, [gateway.gateway_id, device.UID]);

    const handleTimeRangeChange = async ({
        range,
        startDate,
        endDate,
    }: TimeRangeSelection) => {
        let lastTimeValue = Math.round(
            moment.duration(endDate.diff(startDate)).asSeconds()
        );

        if (range === CHART_CUSTOM_RANGE_OPTION) {
            lastTimeValue = Math.round(
                moment.duration(moment().diff(endDate)).asSeconds()
            );

            lastTimeValue = moment
                .duration(endDate.diff(startDate))
                .asSeconds();
        }

        const lastTime = convertSecondsToPrometheusTime(lastTimeValue);
        setTimeRangeSelected({
            range,
            startDate,
            endDate,
        });

        if (typeof onChange === "function") {
            onChange({
                time_range: lastTime,
                //offset: offsetTime,
            });
        }
    };

    const updateAlertStatus = async (checked: boolean) => {
        if (typeof onLoading === "function") {
            onLoading(true);
        }

        if (!alertObject?.uuid) {
            showErrorAlert({
                message: `Sensor is not yet configured.`,
            });
            return;
        }

        const updateResponse: any = await updateAlertNotification(
            gateway.gateway_id,
            device.UID,
            deviceSaid,
            alertObject.uuid,
            { enabled: checked }
        );

        setIsAlert(checked);
        if (typeof onLoading === "function") {
            onLoading(false);
        }

        if (isHttpSuccess(updateResponse.status)) {
            setAlertObject({
                ...alertObject,
                status: checked,
            });

            showSuccessAlert({
                message: `Alert has been ${
                    checked ? "enabled" : "disabled"
                }. It will take up to 2 minutes for the operation to take effect.`,
            });
        } else {
            showErrorAlert(
                getAPIError(updateResponse, "Unable to configure. Try again.")
            );
        }
    };

    return (
        <div>
            <StyledContainer className="form-box">
                <div className="gateway-name">
                    <div className="icon">
                        <img src={pcsIcon} alt="" />
                    </div>
                    <div>
                        {_.get(gateway, "configs[0].sensor_names[0]") ||
                            device.NAME}
                    </div>
                </div>
                <div>
                    <div className="label">Type</div>
                    <div>People Count Sensor</div>
                </div>
                <div>
                    <div className="label">Reading</div>
                    <div className="sensor-value">{sensorData.value}</div>
                </div>
                <div>
                    <div className="label">
                        <div>Min / Max</div>
                        <div style={{ position: "relative", height: "100%" }}>
                            <div
                                style={{
                                    position: "absolute",
                                    top: -20,
                                    left: 0,
                                }}
                            >
                                <TimeRangeDropdown
                                    selectedTimeRange={timeRangeSelected.range}
                                    onChange={handleTimeRangeChange}
                                    maxSpan={{
                                        months: 6,
                                    }}
                                    ranges={timeRanges}
                                />
                            </div>
                        </div>
                    </div>
                    <div>
                        {sensorData.min} / {sensorData.max}
                    </div>
                </div>
                <div>
                    <div className="label">Last Active</div>
                    <div>
                        {moment(lastActive.heartbeat, "X").calendar(
                            getCalendarFormat()
                        )}
                        <span></span>
                    </div>
                </div>
                <div className="action">
                    <div className="label">
                        <span>Alert</span>
                        <span>
                            <HoverAuthorizeTooltip permission="gateway:update">
                                <div>
                                    <Form.Check
                                        type="switch"
                                        id={`switch-${gateway.gateway_id}`}
                                        label=""
                                        checked={isAlert}
                                        disabled={disabledAlert}
                                        onChange={(e: any) => {
                                            const { checked } = e.target;
                                            if (!checked) {
                                                walletModalTrigger(() =>
                                                    updateAlertStatus(checked)
                                                );
                                            } else {
                                                updateAlertStatus(checked);
                                            }
                                        }}
                                    />
                                </div>
                            </HoverAuthorizeTooltip>
                        </span>
                    </div>
                    <div>
                        {device?.UID !== undefined &&
                        device?.PORT !== undefined ? (
                            <Link
                                to={{
                                    pathname: `/sensor-details/${gateway.gateway_id}/LDSBus/${device.PORT}/${device.UID}/${deviceSaid}`,
                                }}
                            >
                                <i className="material-icons right">
                                    keyboard_arrow_right
                                </i>
                            </Link>
                        ) : (
                            ""
                        )}
                    </div>
                </div>
            </StyledContainer>
        </div>
    );
};

export default PSCGatewayDetail;
